import { useState } from "react";
import { Fingerprint } from "lucide-react";
import { authApi } from "../../api/auth";
import type { PasskeyLoginResponse } from "../../api/types";
import { Button } from "../../components/ui";

export async function signInWithPasskey(): Promise<PasskeyLoginResponse> {
  const { startAuthentication } = await import("@simplewebauthn/browser");
  const o = await authApi.passkeyOptions();
  const credential = await startAuthentication({ optionsJSON: o.options });
  return authApi.passkeyVerify(o.challenge_id, credential);
}

export function UsePasswordLink({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="mt-4 w-full text-center text-sm font-medium text-signal hover:text-white" data-testid="admin-login-use-password">
      Use password + authenticator instead
    </button>
  );
}

export function PasskeyButton({ onResult, onUsePassword, variant = "secondary" }: {
  onResult: (next: PasskeyLoginResponse["next_step"]) => void; onUsePassword?: () => void; variant?: "secondary" | "ghost";
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const go = async () => {
    setBusy(true);
    setFailed(false);
    try { onResult((await signInWithPasskey()).next_step); } catch { setFailed(true); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-white/10 pt-6">
      <Button type="button" variant={variant} onClick={go} loading={busy} className="w-full" data-testid="admin-login-passkey-button">
        <Fingerprint className="h-4 w-4" /> Sign in with a passkey
      </Button>
      <p className="mt-2 text-center text-xs text-zinc-500">Face ID, Touch ID, fingerprint or a security key. No email needed.</p>
      {failed && (
        <p className="mt-3 text-center text-sm text-red-300" role="alert" data-testid="admin-login-passkey-error">
          Passkey sign-in didn't work.{" "}
          {onUsePassword && <button type="button" onClick={onUsePassword} className="font-semibold text-signal underline-offset-2 hover:underline" data-testid="admin-login-passkey-use-password">Use password + authenticator instead</button>}
        </p>
      )}
    </div>
  );
}
